import { html, TemplateResult } from 'lit';
import { state, property, query, queryAll } from 'lit/decorators.js';
import type { SelectOption } from '../types';
import { DropdownEvents, ListPosition } from '../types';
import { Keys } from '../../../types/common/types';
import { styles } from '../view/styles';
import { debounce } from '../../../utils/event.utils';
import {
  isBlurEventOutsideElement,
  isElementOverflowed,
  OverflowDirection,
  toggleVisibility,
} from '../../../utils/htmlElement.utils';
import { GzTooltip } from '../../gzTooltip';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { LabelWidth } from '../../../types/common/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { classMap } from 'lit/directives/class-map.js';

export class GzDropdownWebComponent extends FormFieldInput<string | number> {
  static get styles() {
    return styles;
  }

  /**
   * Visible items in dropdown.
   * It's used to compute the max height of the dropdown menu, ie: for 3 items visible the dropdown height will be
   *  3 * var(--list-item-height), where --list-item-height represents the height of a single list item
   */
  @property({ type: Number, attribute: true, reflect: true })
  itemsVisible = 10;

  @state()
  rootStyles;

  @property({ type: Boolean, attribute: true, reflect: true })
  combobox = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  localFiltering = false;

  @state()
  hasResults = true;

  @property({ type: Array, attribute: true, reflect: true })
  options: Array<SelectOption> = [];

  /**
   * This message will be displayed in the dropdown if the input search doesn't
   * return any results. Its value is received as a translated string from
   * the parent, i.e. the DashboardView, via the stringTranslator.
   */
  @property({ type: String, attribute: true, reflect: true })
  noResultsFound = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  invalid = false;

  /**
   * This message will be displayed in the dropdown if we get a backend
   * request error. Its value is received as a translated string from
   * the parent, i.e. the DashboardView, via the stringTranslator.
   */
  @property({ type: String, attribute: true, reflect: true })
  errorMessage = '';

  @property({ type: String, attribute: true, reflect: true })
  value: string | number = '';

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ type: Number, attribute: true, reflect: true })
  keypressDebounceTimeInMs = 700;

  @state()
  private open = false;

  @property({ type: String, attribute: true, reflect: true })
  listPosition = ListPosition.BOTTOM;

  @property({ type: Boolean, attribute: true, reflect: true })
  borderless = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  openOnClear = true;

  /**
   * We will use this property to store the last selected option and restore
   * the input value in case of errors
   */
  @state()
  private lastSelectedOption?: SelectOption | null;

  @property({ type: Object, attribute: true, reflect: true })
  selectedOption: SelectOption | null;

  /**
   * The default dropdown width is 200px, but we can assign a custom width
   */
  @property({ type: Number, attribute: true, reflect: true })
  labelWidth?: LabelWidth;

  /**
   * The activeIndex marks the currently active option in the dropdown
   */
  @state()
  private activeIndex: number;

  /**
   * The focusedIndex marks the option as focused when the selection is
   * performed by keyboard up/down arrows. The user can then press 'Enter' and
   * select the option.
   */
  @state()
  private focusedIndex: number;

  /**
   * The showDeleteIcon property controls the CLEAR (X) button visibility in the combobox input
   */
  @state()
  private showDeleteIcon = false;

  /**
   * The canDelete property controls the CLEAR (X) button visibility in the readOnly input
   * Also the property gives the possibility to reset the dropdown value from readOnly and combobox input
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  public canDelete = false;

  @query('#combo')
  private inputElement: HTMLInputElement;

  @query('#list')
  private readonly dropdownList: HTMLElement;

  @queryAll('.dropdown-list--option')
  private dropdownListOption!: NodeList;

  @query('#deleteButton')
  private deleteButton: HTMLButtonElement;

  debouncedSearchEvent = debounce(() => this.emitInputSearchEvent(), this.keypressDebounceTimeInMs);

  private previousValue: SelectOption | null = null;

  public setFormValue(value: string | number) {
    const optionExists = this.options.filter(option => option.value === value).length > 0;
    this.previousValue = this.selectedOption ? { ...this.selectedOption } : null;
    this.value = value;

    if (!optionExists) {
      this.setInputElementValue('');
    }

    this.setSelectedOption();
    this.emitSelectionEvent();
    this.emitOptionSelectedEvent();
  }

  public getFormValue(): string | number {
    return this.value;
  }

  public setEnabled(isEnabled: boolean): void {
    this.disabled = !isEnabled;
  }

  private renderNoResultsTemplate() {
    return html` <li class="dropdown-list--option no-results disabled">${this.noResultsFound}</li>`;
  }

  private renderLoadingTemplate() {
    return html` <li class="dropdown-list--option disabled spinner">
      <gz-progress-spinner spinnerwidth="small"></gz-progress-spinner>
    </li>`;
  }

  private renderErrorTemplate() {
    return html` <li class="dropdown-list--option error disabled">${this.errorMessage}</li>`;
  }

  private renderInputLabel(): TemplateResult | undefined {
    if (!this.label) {
      return;
    }
    return html`<span class="dropdown-label">${this.label}</span>`;
  }

  private renderOptionsList(options: SelectOption[]) {
    let template;

    if (this.isError) {
      template = this.renderErrorTemplate();
    } else {
      if (!isEmpty(options) && this.hasResults) {
        template = html`${options.map((option, index) => this.renderListItem(option, index))}`;
      } else {
        template = this.renderNoResultsTemplate();
      }
    }

    return template;
  }

  private renderListItem(option: SelectOption, index: number) {
    // this.option is already encoded. However, html`` encodes again, meaning that we'll encode twice
    // (i.e., &amp; will be re-encoded as &&ampamp;) which will result in faulty display. That's why we need
    // to decode option first.
    option = desanitizeObject(option);

    const classes = {
      'dropdown-list--option': true,
      active: `${this.getActiveOptionClass(option)}`,
      focused: `${this.getFocusedOptionClass(option)}`,
    };

    return html`
      <li
        @click="${() => this.updateDropdownSelection(option)}"
        @mousedown="${() => this.updateDropdownSelection(option)}"
        class="${classMap(classes)}"
        id="${option.id ? option.id : index}"
        value="${option.value}"
        role="option"
      >
        <gz-tooltip tooltiptext="${option.title}" isellipsis="true" class="tooltip">
          <span> ${option.title} </span>
        </gz-tooltip>
      </li>
    `;
  }

  private renderEditableInputElement() {
    // this.selectedOption is already encoded. However, html`` encodes again, meaning that we'll encode twice
    // (i.e., &amp; will be re-encoded as &&ampamp;) which will result in faulty display. That's why we need
    // to decode selectedOption first.
    this.selectedOption = desanitizeObject(this.selectedOption);
    const classes = {
      'dropdown-field': true,
      'is-combobox': true,
      invalid: this.invalid,
      'floating-label': this.value || this.value === 0 || this.open,
    };

    return html`
      <label class="dropdown-field ${classMap(classes)}">
        ${this.renderInputLabel()}
        <input
          id="combo"
          type="text"
          class="dropdown-value"
          autocomplete="off"
          @click="${debounce(event => this.handleInputClickForComboBox(event), 150)}"
          @keyup="${this.onKeyUp}"
          @select="${this.onTextSelect}"
          @blur="${this.onInputBlur}"
          .placeholder="${this.getEditableInputPlaceholder()}"
          .value="${this.selectedOption ? this.selectedOption.title : ''}"
        />

        ${this.renderInvalidIcon()}
        <button
          id="deleteButton"
          role="button"
          @click="${debounce(event => this.clearInputValueHandler(event), 150)}"
          class="${this.showDeleteIcon ? 'visible' : ''} dropdown-button dropdown-button--delete icon-button"
        >
          <gz-icon name="ds-icon-clear" color="secondary"></gz-icon>
        </button>

        <button
          @click="${debounce(event => this.clickOnComboboxButton(event), 150)}"
          @keyup="${this.onKeyUp}"
          role="button"
          class="dropdown-button dropdown-button--arrow icon-button"
        >
          <span class="dropdown-arrow"></span>
        </button>
      </label>
    `;
  }

  private getEditableInputPlaceholder(): string {
    return !this.open && !isEmpty(this.label)
      ? ''
      : this.selectedOption && this.selectedOption.title
      ? this.selectedOption.title
      : this.placeholder;
  }

  private renderReadonlyInputElement() {
    this.selectedOption = desanitizeObject(this.selectedOption);
    const classes = {
      invalid: this.invalid,
      'floating-label': this.open || this.value || this.value === 0,
    };

    return html`
      <label class="dropdown-field ${classMap(classes)}" part="dropdownField">
        ${this.renderInputLabel()}
        <input
          id="combo"
          type="text"
          class="dropdown-value"
          readonly
          @click="${this.toggleDropdown}"
          @keydown="${this.onKeyDown}"
          @blur="${this.onInputBlur}"
          .placeholder="${this.getPlaceholder()}"
          .value="${this.selectedOption && this.selectedOption.title ? this.selectedOption.title : ''}"
        />
        ${this.renderInvalidIcon()}
        <button
          id="deleteButton"
          role="button"
          @click="${debounce(event => this.clearInputValueHandler(event), 150)}"
          class="${this.showDeleteIcon && this.canDelete
            ? 'visible'
            : ''} dropdown-button dropdown-button--delete icon-button"
        >
          <gz-icon name="ds-icon-clear" color="secondary"></gz-icon>
        </button>
        <span class="dropdown-button dropdown-button--arrow icon-button">
          <span class="dropdown-arrow"></span>
        </span>
      </label>
    `;
  }

  private getPlaceholder(): string {
    return this.open || !this.label || isEmpty(this.label) ? this.placeholder : '';
  }

  getClassMap() {
    const classList = ['open', 'disabled', 'borderless'];
    const cssClasses = classList.filter(className => this[className]);
    cssClasses.push(this.listPosition);

    return cssClasses;
  }

  render() {
    return html`
      <div
        @mouseenter="${this.handleDropdownMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        class="dropdown ${this.getClassMap().join(' ')}"
      >
        ${this.combobox ? this.renderEditableInputElement() : this.renderReadonlyInputElement()}
        <input id="inputSelectedValue" type="hidden" .value="${this.selectedOption?.title ?? ''}" />

        <div class="dropdown-list">
          <ul id="list" class="dropdown-list--inner list-unstyled">
            ${this.isLoading ? this.renderLoadingTemplate() : this.renderOptionsList(this.options)}
          </ul>
        </div>
      </div>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.rootStyles = getComputedStyle(document.body);
    this.computeDropdownListMaxHeight(this.itemsVisible);

    if (this.combobox) {
      this.setSelectedOption();
    }
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    if (name === 'value') {
      this.updateSelectedOptionFromValue(newVal);
    }

    if (name === 'options' && newVal) {
      this.updateComplete.then(() => {
        this.updateFocusedIndex();
      });
    }
  }

  private computeDropdownListMaxHeight(items: number): void {
    const listItemHeight = this.rootStyles.getPropertyValue('--list-item-height');
    this.style.setProperty('--gz-dropdown-max-height', items * listItemHeight + listItemHeight / 2 + 'px');
  }

  private updateSelectedOptionFromValue(val: string) {
    if (isEmpty(val) && this.canDelete) {
      this.selectedOption = null;
    } else {
      this.setSelectedOption();
    }
  }

  private getResultsFromSearchString(searchString: string): SelectOption[] {
    return this.options.filter((option: SelectOption) => {
      return option.title.toLowerCase().includes(searchString);
    });
  }

  private performClienSideFiltering() {
    if (!this.localFiltering) {
      return;
    }

    const searchResult = this.getResultsFromSearchString(this.inputElement.value.toLowerCase());

    if (isEmpty(searchResult)) {
      this.hasResults = false;
    } else {
      this.hasResults = true;
      this.toggleVisibilityForListItems(searchResult);
    }
  }

  private toggleVisibilityForListItems(searchResult) {
    this.options.forEach((option: SelectOption, index: number) => {
      const item = this.dropdownListOption[index] as HTMLElement;
      if (item) {
        toggleVisibility(item, searchResult.includes(option));
      }
    });
    this.updateFocusedIndex();
  }

  private clickOnComboboxButton(e) {
    if (e.detail > 1) {
      return;
    }
    /**
     * Clear the search string when the user clicks the input and the input is closed.
     */
    if (!this.open) {
      this.updateDropdownState(true);
      this.setInputElementValue('');
      /**
       * If the last selection property is set, we emit the search event
       * using the latest selected option, so we can return the matched
       * items in the dropdown
       */
      if (this.lastSelectedOption) {
        this.setInputElementValue(this.lastSelectedOption.title);
      }

      this.inputElement.focus();
      this.emitInputSearchEvent();
      GzTooltip.hide();
    } else {
      this.updateDropdownState(false);
      this.inputElement.blur();
    }
  }

  handleInputClickForComboBox(event: MouseEvent) {
    /**
     * Cancel the event on multiple clicks on the input
     */
    if (event.detail > 1) {
      return;
    }

    /**
     * Clear the search string when the user clicks the input and the input is closed.
     */
    if (!this.open) {
      this.setInputElementValue('');
      /**
       * If the last selection property is set, we emit the search event
       * using the latest selected option, so we can return the matched
       * items in the dropdown
       */
      if (this.lastSelectedOption) {
        this.setInputElementValue(this.lastSelectedOption.title);
      }

      this.setShowDeleteIcon(this.inputElement.value.length > 0);
      this.performClienSideFiltering();
    }

    /**
     * Open the input and keep it open for as long as the user interacts with it
     */
    this.updateDropdownState(true);
    this.emitInputSearchEvent();
    GzTooltip.hide();
  }

  onInputBlur(event: FocusEvent) {
    event.preventDefault();
    this.handleInputBlur(event);
  }

  handleInputBlur(event?: FocusEvent) {
    if (this.combobox && this.selectedOption) {
      if (this.isError) {
        this.setInputElementValue(this.selectedOption.title);
        this.isError = false;
      } else {
        /**
         *  If the user selects a new option, we will display its value in
         *  the input.
         *  If not, we will display the currently selected option value in the
         *  input
         */
        if (this.lastSelectedOption) {
          this.setInputElementValue(this.lastSelectedOption.title);
          this.performClienSideFiltering();
          this.onUserInput();
        } else {
          this.setInputElementValue(this.selectedOption.title);
        }
        this.deselectTextString();

        /**
         * If the input is blurred by clicking outside the input => close the dropdown
         * event.relatedTarget will return null if the blur happened outside the current element, and will return the
         * related target if the input was blurred by clicking an adjacent element, i.e. the 'arrow' or 'clear' button
         */
        if (!event || !event.relatedTarget) {
          this.updateDropdownState(false);
          return;
        }

        /**
         * If the input is open and the user clicks somewhere inside the selector (i.e., the arrow or the delete
         * button), a blur event will be triggered just before the button click - we need to cancel this event,
         * because this will cause a glitch effect on the dropdown.
         */
        if (this.open && !isBlurEventOutsideElement(event, this)) {
          return;
        }
      }
      this.inputElement.blur();
    }
    this.updateDropdownState(false);
  }

  clearInput() {
    this.lastSelectedOption = undefined;
    this.inputElement.value = '';
    this.onUserInput();
  }

  onUserInput() {
    this.setShowDeleteIcon(this.inputElement.value.length > 0);
  }

  onTextSelect(e: MouseEvent) {
    /**
     * When the user selects the text without clicking the input, open the dropdown and handle the input click events
     */
    if (this.open) {
      return;
    }
    this.handleInputClickForComboBox(e);
  }

  deselectTextString() {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  }

  onKeyUp(event: KeyboardEvent) {
    this.getActionFromKey(event);
    this.performClienSideFiltering();
  }

  onKeyDown(event: KeyboardEvent) {
    this.getActionFromKey(event);
  }

  getActionFromKey(event: KeyboardEvent) {
    const { key } = event;
    const max = this.options.length - 1;
    const listItems = this.dropdownList.querySelectorAll('[role=option]');

    switch (key) {
      case Keys.Down:
        event.preventDefault();
        const incrementIndex = Math.min(max, this.focusedIndex + 1);
        this.setFocusedIndex(incrementIndex);
        this.scrollToFocusedItem(listItems, this.focusedIndex);
        break;

      case Keys.Up:
        event.preventDefault();
        const decrementIndex = Math.max(0, this.focusedIndex - 1);
        this.setFocusedIndex(decrementIndex);
        this.scrollToFocusedItem(listItems, this.focusedIndex);
        break;

      case Keys.Enter:
        this.updateDropdownSelection(this.options[this.focusedIndex]);
        break;

      case Keys.Escape:
        this.handleInputBlur();
        break;

      default:
        this.onUserInput();
        this.debouncedSearchEvent();
    }
  }

  clearInputValueHandler(e: Event) {
    e.preventDefault();
    this.clearInputValue();
  }

  public clearInputValue() {
    /**
     * Clear the search string, open the input and keep it open for as long as the user
     * interacts with it and emit the search event
     */
    this.setInputElementValue('');
    if (this.canDelete) {
      this.setFormValue('');
      this.lastSelectedOption = null;
    }
    this.performClienSideFiltering();

    if (this.openOnClear) {
      this.updateDropdownState(true);
      this.inputElement.focus();
    }

    this.emitInputSearchEvent();
    this.setShowDeleteIcon(false);
    this.updateFocusedIndex();
    GzTooltip.hide();
  }

  toggleDropdown() {
    this.open = !this.open;
    this.emitToggleDropdownEvent(this.open);

    this.updateActiveFocusedIndex();
  }

  private emitToggleDropdownEvent(isOpen: boolean) {
    isOpen ? this.dispatchEvent(new CustomEvent('open')) : this.dispatchEvent(new CustomEvent('close'));
  }

  updateActiveFocusedIndex() {
    if (this.open && this.selectedOption) {
      const selectedIndex = this.getOptionIndex(this.selectedOption);
      this.setActiveIndex(selectedIndex);
      this.setFocusedIndex(selectedIndex);
    }
  }

  updateDropdownState(isOpen: boolean) {
    if (isOpen && isOpen === this.open) {
      return;
    }

    this.open = isOpen;
    this.emitToggleDropdownEvent(this.open);
    this.updateActiveFocusedIndex();
  }

  updateDropdownSelection(option: SelectOption) {
    if (!option) {
      return;
    }

    this.previousValue = this.selectedOption ? { ...this.selectedOption } : null;
    if (option.value !== undefined) {
      this.value = option.value;
    }
    this.selectedOption = option;

    if (this.combobox) this.setLastSelectedOption();

    this.emitSelectionEvent();
    this.emitOptionSelectedEvent();
    this.updateDropdownState(false);
    this.setShowDeleteIcon(true);
    this.inputElement.blur();
  }

  setLastSelectedOption() {
    if (this.selectedOption) {
      this.lastSelectedOption = this.selectedOption;
    }
  }

  setInputElementValue(value: string) {
    if (this.inputElement) {
      this.inputElement.value = value;
    }
  }

  setActiveIndex(index: number) {
    this.activeIndex = index;
  }

  setShowDeleteIcon(value: boolean) {
    this.showDeleteIcon = value;
  }

  selectOption(option: SelectOption | null) {
    this.selectedOption = option;
  }

  setSelectedOption() {
    const selected = this.options.filter((option: SelectOption) => option.value === this.value);

    if (selected.length === 0) {
      this.combobox && !this.canDelete ? this.selectOption(this.options[0]) : this.selectOption(null);
    } else {
      if (selected.length > 1) {
        console.error(`More than one option matching the given value: ${this.value}. ` + `Will select the first one.`);
      }
      this.selectOption(selected[0]);
    }
  }

  getOptionIndex(option: SelectOption | null): number {
    const res = this.options.findIndex((item: SelectOption) => item.value === option?.value);
    return res !== -1 ? res : 0;
  }

  emitSelectionEvent() {
    const event = new CustomEvent('selectionChanged', {
      detail: {
        ...this.selectedOption,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  emitOptionSelectedEvent() {
    const event = new CustomEvent(DropdownEvents.OPTION_SELECTED, {
      detail: {
        oldValue: this.previousValue,
        newValue: this.selectedOption,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  emitInputSearchEvent() {
    if (!this.localFiltering) {
      const event = new CustomEvent('onNewInputString', {
        detail: {
          searchString: this.inputElement.value,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(event);
    }
  }

  getActiveOptionClass(option: SelectOption): string {
    return this.selectedOption && this.selectedOption.value === option.value ? 'active' : '';
  }

  setFocusedIndex(index) {
    this.focusedIndex = index;
  }

  updateFocusedIndex() {
    /**
     * Options might not contain the selected option, i.e. the user has 'Bitdefender' selected, then performs
     * another search for 'Vagrant' => the options list will not contain the currently selected option. In this
     * case, the focus needs to be on the first item in the options list.
     */

    const hasSelectedOption =
      this.selectedOption && this.options.filter(option => option.value === this.selectedOption?.value).length > 0;

    if (hasSelectedOption) {
      this.setFocusedIndex(this.getOptionIndex(this.selectedOption));
    } else {
      this.setFocusedIndex(0);
    }
  }

  getFocusedOptionClass(option: SelectOption): string {
    const optionIndex = this.getOptionIndex(option);
    return optionIndex === this.focusedIndex ? 'focused' : '';
  }

  listIsScrollable(element: HTMLElement): boolean {
    return element && element.clientHeight < element.scrollHeight;
  }

  /**
   * Ensure given child element is within the parent's visible scroll area
   */
  maintainScrollVisibility(activeElement: HTMLElement, scrollParent: HTMLElement) {
    if (!activeElement) {
      return;
    }

    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
      scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
      scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
  }

  /**
   * Scroll to focused item
   */
  scrollToFocusedItem(itemsList: NodeList, index: number) {
    if (this.open && this.listIsScrollable(this.dropdownList)) {
      const item = itemsList[index] as HTMLElement;
      this.maintainScrollVisibility(item, this.dropdownList);
    }
  }

  private handleDropdownMouseEnter(event: MouseEvent) {
    if (this.open || !isElementOverflowed(this.inputElement, OverflowDirection.HORIZONTAL)) {
      return;
    }
    const tooltipText = this.selectedOption ? this.selectedOption.title : null;

    if (tooltipText) {
      GzTooltip.show(tooltipText, event);
    }
  }

  public renderInvalidIcon() {
    if (this.invalid || this.isError) {
      return html`<gz-icon name="ds-icon-notification_negative_S" color="negative" size="24"></gz-icon>`;
    }
  }
}
